import {
	Box,
	Card,
	CardContent,
	Grid,
	IconButton,
	ListItem,
	ListItemIcon,
	ListItemText,
	Snackbar,
	Tooltip,
	Typography,
	makeStyles,
} from '@material-ui/core';
import React, { useState } from 'react';
import GeocoderForm from './geocoder-form.component';
import { IGeocoderRequest, IGeocoderResponse } from 'src/models';
import { useTranslation } from 'react-i18next';
import { GeocoderService } from 'src/services/geocoder.service';
import { FileCopyOutlined, Info } from '@material-ui/icons';
import Alert, { Color } from '@material-ui/lab/Alert';
import { Constant } from 'src/shared/constants';

export const useStyles = makeStyles({
	hidden: {
		display: 'none',
	},
	visible: {
		display: 'block',
	},
});

const GeocoderTool: React.FC = () => {
	const { t } = useTranslation();
	const classes = useStyles();

	// useStates are created here.
	const [geocoder, setGeocoder] = useState<IGeocoderResponse | null>(null);
	const [snackbarState, setSnackbarState] = useState<boolean>(false);
	const [snackbarMessage, setSnackbarMessage] = useState<string>('');
	const [severity, setSeverity] = useState<Color>(Constant.colorError);

	// Function to use GeocoderService and the handle the response from the service and show it onto the screen
	const getGeocoder = async (value: IGeocoderRequest) => {
		try {
			const res = await GeocoderService.getGeocoder(value);
			let geocoderFromResponse: IGeocoderResponse = {
				latitude: res.data?.latitude,
				longitude: res.data?.longitude,
				addressLine1: res.data?.addressLine1,
				addressLine2: res.data?.addressLine2,
				pincode: res.data?.pincode,
				city: res.data?.city,
				state: res.data?.state,
				stateCode: res.data?.stateCode,
				country: res.data?.country,
				countryCode: res.data?.countryCode,
			};

			// set the geocoder to show into the UI
			setGeocoder(geocoderFromResponse);
		} catch (error: any) {
			setSnackbarMessage(error);
			setSeverity(Constant.colorError);
			setSnackbarState(true);
		}
	};

	// Function to handle data copy
	const handleCopy = async (textToCopy: string, copiedField: string) => {
		try {
			// writing textToCopy on the clipboard
			await navigator.clipboard.writeText(textToCopy);

			// showing snackbar
			setSnackbarMessage(`${copiedField} copied.`);
			setSeverity(Constant.colorInfo);
			setSnackbarState(true);
		} catch (err) {}
	};

	const makeCompoundValue = (value1: string | null | undefined, value2: string | null | undefined) => {
		let value = '';
		if (value1) value += value1 + (value2 ? ' - ' : '');
		if (value2) value += value2;

		return value;
	};

	return (
		<>
			<Card>
				<ListItem className='pt-20'>
					<ListItemIcon className='mr-10' style={{ minWidth: '15px' }}>
						{<Info />}
					</ListItemIcon>
					<ListItemText>
						<Typography>{t('message-enter-pincode-or-address-fields')}</Typography>
					</ListItemText>
				</ListItem>
				<CardContent>
					<Grid item container spacing={5}>
						{/* Form to enter the address on which the geocoder data will be fetched from API */}
						<Grid item sm={7}>
							<GeocoderForm getGeocoder={getGeocoder} setGeocoder={setGeocoder} />
						</Grid>

						{/* Output section where the output from the API is shown, and if there is not data then this section is not displayed to user. */}
						<Grid item sm={5} className={geocoder ? classes.visible : classes.hidden}>
							<Grid item container sm={12}>
								<Grid item sm={5}>
									<Typography variant='subtitle1' color='inherit'>
										<Box fontWeight='bold'>{t('label-address-line-1')}:</Box>
									</Typography>
								</Grid>
								<Grid item sm={7}>
									{geocoder?.addressLine1 ? (
										<Typography variant='subtitle2' color='inherit'>
											{geocoder.addressLine1}
										</Typography>
									) : (
										Constant.na
									)}
								</Grid>
							</Grid>

							<Grid item container sm={12}>
								<Grid item sm={5}>
									<Typography variant='subtitle1' color='inherit'>
										<Box fontWeight='bold'>{t('label-address-line-2')}:</Box>
									</Typography>
								</Grid>
								<Grid item sm={7}>
									{geocoder?.addressLine2 ? (
										<Typography variant='subtitle2' color='inherit'>
											{geocoder.addressLine2}
										</Typography>
									) : (
										Constant.na
									)}
								</Grid>
							</Grid>

							<Grid item container sm={12}>
								<Grid item sm={5}>
									<Typography variant='subtitle1' color='inherit'>
										<Box fontWeight='bold'>{t('label-city')}:</Box>
									</Typography>
								</Grid>
								<Grid item sm={7}>
									{geocoder?.city ? (
										<Typography variant='subtitle2' color='inherit'>
											{geocoder.city}
										</Typography>
									) : (
										Constant.na
									)}
								</Grid>
							</Grid>

							<Grid item container sm={12}>
								<Grid item sm={5}>
									<Typography variant='subtitle1' color='inherit'>
										<Box fontWeight='bold'>{t('label-state')}:</Box>
									</Typography>
								</Grid>
								<Grid item sm={7}>
									{geocoder?.state || geocoder?.stateCode ? (
										<Typography variant='subtitle2' color='inherit'>
											{makeCompoundValue(geocoder?.stateCode, geocoder?.state)}
										</Typography>
									) : (
										Constant.na
									)}
								</Grid>
							</Grid>

							<Grid item container sm={12}>
								<Grid item sm={5}>
									<Typography variant='subtitle1' color='inherit'>
										<Box fontWeight='bold'>{t('label-country')}:</Box>
									</Typography>
								</Grid>
								<Grid item sm={7}>
									{geocoder?.country || geocoder?.countryCode ? (
										<Typography variant='subtitle2' color='inherit'>
											{makeCompoundValue(geocoder?.countryCode, geocoder?.country)}
										</Typography>
									) : (
										Constant.na
									)}
								</Grid>
							</Grid>
							<Grid item container sm={12}>
								<Grid item sm={5}>
									<Typography variant='subtitle1' color='inherit'>
										<Box fontWeight='bold'>{t('label-pincode')}:</Box>
									</Typography>
								</Grid>
								<Grid item sm={7}>
									{geocoder?.pincode ? (
										<Typography variant='subtitle2' color='inherit'>
											{geocoder.pincode}
										</Typography>
									) : (
										Constant.na
									)}
								</Grid>
							</Grid>

							<Grid item container sm={12}>
								<Grid item sm={5}>
									<Typography variant='subtitle1' color='inherit'>
										<Box fontWeight='bold'>{t('label-latitude')}:</Box>
									</Typography>
								</Grid>
								<Grid item sm={7}>
									{geocoder?.latitude ? (
										<Typography variant='subtitle2' color='inherit'>
											{geocoder.latitude}
											<Tooltip title='Copy' placement='top' arrow={true}>
												<IconButton
													className='mb-3 ml-10'
													onClick={() =>
														handleCopy(geocoder?.latitude ? geocoder?.latitude?.toString() : '', t('label-latitude'))
													}
													size='small'
												>
													{<FileCopyOutlined className='font17px' />}
												</IconButton>
											</Tooltip>
										</Typography>
									) : (
										Constant.na
									)}
								</Grid>
							</Grid>

							<Grid item container sm={12}>
								<Grid item sm={5}>
									<Typography variant='subtitle1' color='inherit'>
										<Box fontWeight='bold'>{t('label-longitude')}:</Box>
									</Typography>
								</Grid>
								<Grid item sm={7}>
									{geocoder?.longitude ? (
										<Typography variant='subtitle2' color='inherit'>
											{geocoder.longitude}
											<Tooltip title='Copy' placement='top' arrow={true}>
												<IconButton
													className='mb-3 ml-10'
													onClick={() =>
														handleCopy(geocoder?.longitude ? geocoder?.longitude?.toString() : '', t('label-longitude'))
													}
													size='small'
												>
													{<FileCopyOutlined className='font17px' />}
												</IconButton>
											</Tooltip>
										</Typography>
									) : (
										Constant.na
									)}
								</Grid>
							</Grid>
						</Grid>
					</Grid>
				</CardContent>
			</Card>
			<Snackbar open={snackbarState} autoHideDuration={6000} onClose={() => setSnackbarState(false)}>
				<Alert severity={severity}>{snackbarMessage}</Alert>
			</Snackbar>
		</>
	);
};

export default GeocoderTool;
